import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StaticService } from '../services/static.service';
import { catchError, map, of } from 'rxjs';

/**
 * Blocks navigation to a screen if the current user is not authorised for that screen.
 * Authorisation is determined using the SCREENS map fetched via StaticService
 * and cached in sessionStorage under the key "SCREENS".
 *
 * Route usage:
 *   { path: 'screen/EMPLOYEES', component: EmployeesComponent, canActivate: [authoriseGuard], data: { screenKey: 'EMPLOYEES' } }
 *   { path: 'screen/:name', component: StaticScreenComponent, canActivate: [authoriseGuard] }
 */
export const authoriseGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const staticService = inject(StaticService);

  // Determine the target screen key
  const keyFromData = (route.data?.['screenKey'] as string | undefined) ?? '';
  const keyFromParam = (route.paramMap.get('name') || '').toUpperCase();
  const screenKey = (keyFromData || keyFromParam || '').toUpperCase();

  // If no key known, allow
  if (!screenKey) {
    return true;
  }

  const isAllowed = (mapObj: Record<string, string> | null | undefined): boolean => {
    try {
      if (!mapObj) return false;
      return Object.prototype.hasOwnProperty.call(mapObj, screenKey);
    } catch {
      return false;
    }
  };

  // First try session cache
  const cached = sessionStorage.getItem('SCREENS');
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as Record<string, string>;
      if (isAllowed(parsed)) {
        return true;
      }
      router.navigate(['screen/DASHBOARD']);
      return false;
    } catch {
      // fallthrough to live fetch
    }
  }

  // Fallback to live fetch
  return staticService.getScreensMap().pipe(
    map((mapObj) => {
      if (isAllowed(mapObj)) {
        return true;
      }
      router.navigate(['screen/DASHBOARD']);
      return false;
    }),
    catchError(() => {
      router.navigate(['screen/DASHBOARD']);
      return of(false);
    })
  );
};


